import { useEffect, useState } from 'react';
import { useTOCContext } from '@contexts/ToC';
import type { HeadingRefs } from '@models/Toc';
import { useUrlHashContext } from '@contexts/UrlHash';

export function useActiveAnchorId(): { activeAnchorId: string } {
  const [activeAnchorId, setActiveAnchorId] = useState<string>('');
  const { formattedHeadings, headingRefs } = useTOCContext();
  const { hash, setHashScrolling } = useUrlHashContext();

  useEffect(() => {
    function updateActiveAnchorId() {
      const activeAnchor = getActiveAnchor(headingRefs, { anchorTopOffset: 0 });
      if (activeAnchor) {
        if (activeAnchor?.id === decodeURIComponent(hash)) {
          setHashScrolling(false);
        }
        setActiveAnchorId(activeAnchor.id);
      }
    }

    document.addEventListener('scroll', updateActiveAnchorId);
    document.addEventListener('resize', updateActiveAnchorId);

    updateActiveAnchorId();

    return () => {
      document.removeEventListener('scroll', updateActiveAnchorId);
      document.removeEventListener('resize', updateActiveAnchorId);
    };
  }, [formattedHeadings, headingRefs, hash]);

  return { activeAnchorId };
}

function getActiveAnchor(anchors: HeadingRefs, { anchorTopOffset }: { anchorTopOffset: number }): Element | null {
  const nextVisibleAnchor = anchors.find((anchor) => {
    if (anchor.current) {
      const boundingRect = getVisibleBoundingClientRect(anchor.current);
      return boundingRect.top >= anchorTopOffset;
    }
    return false;
  });

  if (nextVisibleAnchor) {
    const boundingRect = getVisibleBoundingClientRect(nextVisibleAnchor.current);

    if (isInViewportTopHalf(boundingRect)) {
      return nextVisibleAnchor.current;
    }

    return anchors[anchors.indexOf(nextVisibleAnchor) - 1]?.current ?? null;
  }

  return anchors[anchors.length - 1]?.current ?? null;
}

function getVisibleBoundingClientRect(element: HTMLElement): DOMRect {
  const rect = element.getBoundingClientRect();
  const hasNoHeight = rect.top === rect.bottom;

  if (hasNoHeight) {
    return getVisibleBoundingClientRect(element.parentNode as HTMLElement);
  }

  return rect;
}

function isInViewportTopHalf(boundingRect: DOMRect) {
  return boundingRect.top > 0 && boundingRect.bottom < window.innerHeight / 3;
}
